import { zodResolver } from '@hookform/resolvers/zod'
import { merge } from 'lodash'
import { t } from '@lingui/core/macro'
import { useForm } from 'react-hook-form'
import { isValidPhoneNumber, type Country } from 'react-phone-number-input'
import { z } from 'zod'

// `boolean().refine(v => v === true)` (rather than `literal(true)`) keeps the inferred type as
// `boolean`, so the unchecked `false` default values remain valid while the checkbox is still
// required to be checked at validation time.
const mustBeChecked = () => z.boolean().refine((value) => value === true)

const schema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1),
  isGCUAccepted: mustBeChecked(),
  isCarteVitaleAvailable: mustBeChecked(),
  lastName: z.string().min(1),
  countryCode: z.string().optional(),
  phone: z.string().optional(),
})

export type PatientInfoFormData = z.infer<typeof schema>

export function usePatientInfoForm(initialValues?: PatientInfoFormData) {
  return useForm<PatientInfoFormData>({
    defaultValues: merge(
      {
        email: '',
        firstName: '',
        isGCUAccepted: false,
        isCarteVitaleAvailable: false,
        lastName: '',
        countryCode: 'FR',
        phone: '',
        comment: '',
        diagnostic: '',
        files: [],
      },
      initialValues
    ),
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(
      // Phone is optional, but when present it must be valid for the selected country — a
      // cross-field rule, so it lives on the object via superRefine rather than the field.
      schema.superRefine((data, ctx) => {
        if (data.phone && !isValidPhoneNumber(data.phone, data.countryCode as Country)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone'], message: t`Invalid phone number` })
        }
      })
    ),
  })
}
